/**
 * What to say when a lazily loaded view fails to arrive (September audit).
 *
 * The one fallback used to read "This part of the app could not be loaded. A
 * new version may have been installed since this tab was opened." for every
 * failure — a dropped request on the dev proxy, a phone that just lost
 * signal, a render error inside the view — so people were told about updates
 * that never happened, and offline it stacked under two other banners that
 * said something else. Four cases, told apart with what the browser can
 * actually know:
 *
 * - `offline`: the error is a chunk fetch and `navigator.onLine` is false.
 * - `updated`: the error is a chunk fetch, the connection is up, and the
 *   index.html the server serves NOW no longer names the entry script this
 *   document booted with — a deploy happened under this tab.
 * - `network`: the error is a chunk fetch and the served index still names
 *   our entry script, or could not be read: the chunk exists, the request
 *   did not get through.
 * - `crash`: not a chunk fetch at all — a render error under the boundary.
 */
export type ChunkFailureKind = "offline" | "updated" | "network" | "crash";

// The messages Chromium, Firefox, Safari and Vite's preload helper produce for
// a dynamic import or its CSS that did not load. Matched loosely on purpose:
// the exact wording differs by browser and by whether it was the script or
// the stylesheet.
const CHUNK_ERROR_PATTERNS = [
  /dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /loading (css )?chunk/i,
  /module script failed/i
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * The entry script this document booted with, without any query string — the
 * one thing a freshly served index.html either still names or no longer does.
 * Vite's build writes it as `/assets/index-<hash>.js`; the dev server as
 * `/src/main.tsx`.
 */
export function currentEntryScript(doc: Pick<Document, "querySelector"> = document): string | null {
  const element = doc.querySelector('script[type="module"][src]');
  const src = element?.getAttribute("src") ?? "";
  const path = src.split("?")[0] ?? "";
  return path || null;
}

export function classifyChunkFailure(input: {
  error: unknown;
  online: boolean;
  /** The index.html the server serves now, or null when it could not be read. */
  servedIndex: string | null;
  entryScript: string | null;
}): ChunkFailureKind {
  if (!isChunkLoadError(input.error)) return "crash";
  if (!input.online) return "offline";
  if (input.servedIndex !== null && input.entryScript && !input.servedIndex.includes(input.entryScript)) return "updated";
  return "network";
}

/** The index.html as served right now, bypassing every cache; null when that read fails too. */
export async function fetchServedIndex(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const response = await fetchImpl("/", { cache: "no-store", credentials: "include", headers: { accept: "text/html" } });
    if (!response.ok) return null;
    return await response.text();
  } catch {
    return null;
  }
}
